import { useEffect } from "react";

/** A field the user searches with (not a form field that happens to be text). */
export const isSearchInput = (element: Element | null) => {
  if (!(element instanceof HTMLInputElement)) return false;
  if (element.type === "search" || element.getAttribute("role") === "searchbox")
    return true;
  const label = `${element.getAttribute("aria-label") ?? ""} ${element.placeholder ?? ""}`;
  return (
    /^\s*search\b/i.test(label) ||
    /\bsearch\b/i.test(element.getAttribute("aria-label") ?? "")
  );
};

/** Set an input's value the way typing does, so React's onChange runs. */
const clearControlledInput = (input: HTMLInputElement) => {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  )?.set;
  setter?.call(input, "");
  input.dispatchEvent(new Event("input", { bubbles: true }));
};

/**
 * Esc in any search field with text clears it, everywhere in the app. It
 * runs before dialogs and popovers see the key, so inside a dialog the first
 * Esc clears the search and the next one closes the dialog.
 */
export const useSearchEscapeToClear = () => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (event.isComposing) return;
      const target = event.target as Element | null;
      if (!isSearchInput(target)) return;
      const input = target as HTMLInputElement;
      if (!input.value) return;
      event.preventDefault();
      event.stopPropagation();
      clearControlledInput(input);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);
};
